<template>
    <div class="sc__analysis__tab__box tab-content">
        <!-- Header -->
        <div class="sc__analysis__head__box">
            <div class="title__box">
                <div class="head__title__group">
                    <h3 class="title">Starting Targets Decision Box</h3>
                    <p>System-generated report based on group scores. Highlights show suggested starting SC levels and progression paths.</p>
                    <p>{{ EESA_SCOPE_NOTE }}</p>
                </div>
                <div class="print__data__btn">
                    <WorkpacketPdf button-label="Download" :assessmentId="assessmentId" :organizationId="organizationId"
                        :learnerId="learnerId" v-if="assessmentId && organizationId && learnerId" />
                </div>
            </div>
        </div>

        <!-- No echoic targets indicated: the Guide's alternative, quoted -->
        <NoTargetsPanel v-if="noTargets" />

        <!-- SC Analysis Sections -->
        <div class="sc__analysis__data__box">
            <div class="tabs__data__wrapper starting__targets__data__box starting__targets__checkbox">
                <div class="data__title__box">
                    <span class="right__text">If Group score is below 80%</span>
                    <span class="center__text">If Group score is at or above 80%</span>
                </div>
                <div class="divider__sepline"></div>

                <div class="target__guide__data__box">
                    <div v-for="i in 5" :key="i" class="check__group__box"
                        :class="{ active__check__group: groupActivation[i] }">
                        <h6 class="group__title">Group {{ i }}</h6>
                        <template v-for="(checkbox, idx) in checkboxConfig[i]" :key="idx">
                            <div class="input__checkbox">
                                <input type="checkbox" class="disabled__checkbox" disabled
                                    :id="`check__input_${i}_${idx + 1}`" :checked="checkbox.condition" />
                                <label :for="`check__input_${i}_${idx + 1}`" v-html="checkbox.label"></label>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import api from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { handleError } from '@/utils/handleError';
import WorkpacketPdf from './WorkpacketPdf.vue';
import NoTargetsPanel from '@/components/assesments/NoTargetsPanel.vue';
import { EESA_SCOPE_NOTE } from '@/utils/assessment-notes';
import { noTargetsIndicated } from '@/utils/assessment-targets';

const props = defineProps({
    organizationId: Number,
    assessmentId: Number,
    learnerId: Number
});

const loader = useLoaderStore();
const groupScores = ref({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });

const fetchAssessment = async () => {
    loader.show();
    try {
        const { data } = await api.get(
            `/organizations/${props.organizationId}/assessments/${props.assessmentId}`
        );
        const groupScoreData = data?.data?.result?.group_scores || {};
        for (let i = 1; i <= 5; i++) {
            const scoreObj = groupScoreData[i];
            groupScores.value[i] =
                scoreObj?.total > 0 ? (scoreObj.score_one / scoreObj.total) * 100 : 0;
        }
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

const noTargets = computed(() => noTargetsIndicated(groupScores.value));

const activeTargetIndex = computed(() => {
    const s = groupScores.value;

    // If Group 1 < 80 → active is 1
    if (s[1] < 80) return 1;

    // Walk through 2..5
    for (let i = 2; i <= 5; i++) {
        // If previous wasn't ≥ 80, keep last one active
        if (s[i - 1] < 80) return i - 1;

        // Previous ≥ 80 and current < 80 → current is active
        if (s[i] < 80) return i;
    }

    // All groups ≥ 80 → last one active
    return 5;
});

// Precompute activations without recursion
const groupActivation = computed(() => {
    const idx = activeTargetIndex.value;
    return {
        1: idx === 1,
        2: idx === 2,
        3: idx === 3,
        4: idx === 4,
        5: idx === 5
    };
});

// Checkbox configuration by group
const checkboxConfig = computed(() => ({
    1: [
        {
            condition: groupScores.value[1] < 80 || groupScores.value[1] >= 80,
            label: 'Start targets at this SC level'
        },
        {
            condition: groupScores.value[1] >= 80 && (groupScores.value[2] < 80 || groupScores.value[2] >= 80),
            label: 'See Group 2 for guidance'
        }
    ],
    2: [
        {
            condition: groupScores.value[1] >= 80 && (groupScores.value[2] < 80 || groupScores.value[2] >= 80),
            label: 'Start targets at this SC level or lower'
        },
        {
            condition: groupScores.value[2] >= 80 && (groupScores.value[3] < 80 || groupScores.value[3] >= 80),
            label: 'See Group 3 for guidance'
        }
    ],
    3: [
        {
            condition: groupScores.value[2] >= 80 && (groupScores.value[3] < 80 || groupScores.value[3] >= 80),
            label: 'Start targets at this SC level or lower'
        },
        {
            condition: groupScores.value[3] >= 80 && (groupScores.value[4] < 80 || groupScores.value[4] >= 80),
            label: 'See Group 4 for guidance'
        }
    ],
    4: [
        {
            condition: groupScores.value[3] >= 80 && (groupScores.value[4] < 80 || groupScores.value[4] >= 80),
            label:
                'Target highest-achieved SC Levels <span class="check__info">(Probable Group 2 or 1) and Toggle* between 2 and 3 syllable combos; advance to 3 - 4</span>'
        },
        {
            condition: groupScores.value[4] >= 80 && (groupScores.value[5] < 80 || groupScores.value[5] >= 80),
            label:
                'Target highest-achieved SC Levels <span class="check__info">(Probable Group 3 or 2) and Toggle* between 3 and 4 syllable combos; advance to 4 - 5</span>'
        }
    ],
    5: [
        {
            condition: groupScores.value[4] >= 80 && (groupScores.value[5] < 80 || groupScores.value[5] >= 80),
            label:
                'Target highest-achieved SC Levels <span class="check__info">(Probable Group 3 or 2) and Toggle* between 3 and 4 syllable combos; advance to 4 - 5, 5 - 6, and 6+</span>'
        },
        {
            condition: groupScores.value[5] >= 80,
            label:
                'If all Group scores are high, specific intervention for echoic skills may not be needed (See guide, Chapter 3, EESA - R General Information)'
        }
    ]
}));

onMounted(fetchAssessment);
</script>
